import { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  complaintOptions,
  useComplaintStore,
} from "./ComplaintStore/useComplaintStore";
import { normalizeWardIdForPayload } from "../../../Apis/complaintApi";
import ComplaintMapView from "./ComplaintMapView";
import {
  DEFAULT_MAP_CENTER,
  FileUploadField,
  SELECT_MENU_PORTAL_STYLES,
  cancelButtonStyle,
  getDefaultMapCoordinates,
  isValidComplaintImageFile,
  markAllFieldsTouched,
  requiredMark,
  saveButtonStyle,
  sectionHeaderStyle,
} from "./complaintFormShared";

const emptyFormValues = {
  cgNo: "",
  wardId: "",
  streetLocation: "",
  address: "",
  actionRemarks: "",
  latitude: "",
  longitude: "",
  zoneId: "",
  status: "",
  complaintImageFile: null,
};

const validationSchema = Yup.object().shape({
  cgNo: Yup.string().trim().required("Complaint No is required"),
  wardId: Yup.string().required("Revised Ward No is required"),
  streetLocation: Yup.string()
    .trim()
    .required("Street Location is required")
    .max(200, "Maximum 200 characters allowed"),
  status: Yup.string(),
  actionRemarks: Yup.string()
    .trim()
    .required("Action Remarks is required")
    .min(10, "Action Remarks must be at least 10 characters")
    .max(1000, "Maximum 1000 characters allowed"),
  address: Yup.string()
    .trim()
    .required("Address is required")
    .max(500, "Maximum 500 characters allowed"),
  complaintImageFile: Yup.mixed()
    .required("Upload File is required")
    .test(
      "fileType",
      "Only JPG, JPEG, and PNG images are allowed",
      (file) => isValidComplaintImageFile(file)
    ),
});

const buildEditFormValues = (editRow, coordinates = getDefaultMapCoordinates()) => ({
  ...emptyFormValues,
  cgNo: editRow?.cgNo || "",
  wardId: editRow?.wardId?.toString() || editRow?.circleId?.toString() || "",
  streetLocation: editRow?.colonyName || editRow?.streetLocation || "",
  status: "Closed",
  latitude: coordinates.latitude,
  longitude: coordinates.longitude,
});

const EditComplaintForm = ({
  showModal,
  setShowModal,
  editRow,
  wards,
}) => {
  const { isSaving, saveComplaint } = useComplaintStore();
  const [locationLoading, setLocationLoading] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("No file chosen");
  const [isChooseFileHovered, setIsChooseFileHovered] = useState(false);
  const formikRef = useRef(null);
  const userPickedLocationRef = useRef(false);
  const wasModalOpenRef = useRef(false);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    const justOpened = showModal && !wasModalOpenRef.current;
    const justClosed = !showModal && wasModalOpenRef.current;

    if (justClosed) {
      setMapReady(false);
      formikRef.current?.resetForm({ values: emptyFormValues });
      setSelectedFileName("No file chosen");
      setCurrentLocation(null);
      setIsChooseFileHovered(false);
      userPickedLocationRef.current = false;
    }

    if (justOpened && editRow) {
      userPickedLocationRef.current = false;
      formikRef.current?.resetForm({
        values: buildEditFormValues(editRow),
      });
      setSelectedFileName("No file chosen");

      if (navigator.geolocation) {
        setLocationLoading(true);
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const nextLocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            setCurrentLocation(nextLocation);
            setLocationLoading(false);

            if (!userPickedLocationRef.current) {
              const coords = getDefaultMapCoordinates(nextLocation);
              formikRef.current?.setFieldValue("latitude", coords.latitude);
              formikRef.current?.setFieldValue("longitude", coords.longitude);
            }
          },
          () => setLocationLoading(false),
          { enableHighAccuracy: true, timeout: 10000 }
        );
      }
    }

    wasModalOpenRef.current = showModal;
  }, [showModal, editRow]);

  useEffect(() => {
    if (!showModal || userPickedLocationRef.current) return;

    const { latitude, longitude } = getDefaultMapCoordinates(currentLocation);
    formikRef.current?.setFieldValue("latitude", latitude);
    formikRef.current?.setFieldValue("longitude", longitude);
  }, [showModal, currentLocation]);

  const handleClose = () => setShowModal(false);

  const getWardOptionValue = (ward) =>
    normalizeWardIdForPayload(ward.wardId ?? ward.circleId ?? "");

  const getWardName = (wardId) => {
    const ward = wards.find(
      (item) => getWardOptionValue(item) === normalizeWardIdForPayload(wardId)
    );
    return ward?.wardName || ward?.circleName || ward?.wardNo || "";
  };

  const getZoneIdFromWard = (wardId) => {
    const ward = wards.find(
      (item) => getWardOptionValue(item) === normalizeWardIdForPayload(wardId)
    );
    return ward?.zoneId?.toString() || "";
  };

  const handleSubmit = async (values) => {
    const defaultCoordinates = getDefaultMapCoordinates(currentLocation);
    const latitude = values.latitude || defaultCoordinates.latitude;
    const longitude = values.longitude || defaultCoordinates.longitude;
    const wardName = getWardName(values.wardId);

    const updatePayload = {
      complaintId: editRow.complaintId,
      cgNo: values.cgNo || editRow.cgNo || "",
      wardId: normalizeWardIdForPayload(values.wardId),
      revisedWardNo: wardName,
      streetLocation: values.streetLocation?.trim() || "",
      status: values.status || "",
      actionRemarks: values.actionRemarks?.trim() || "",
      address: values.address?.trim() || "",
      latitude,
      longitude,
      complaintImageFile: values.complaintImageFile,
    };

    const result = await saveComplaint(updatePayload, true);
    if (result?.success) {
      handleClose();
    }
  };

  return (
    <Modal
      size="xl"
      show={showModal}
      onHide={handleClose}
      onEntered={() => setMapReady(true)}
      onExited={() => setMapReady(false)}
      mountOnEnter
      unmountOnExit
      enforceFocus={false}
      aria-labelledby="edit-complaint-modal-title"
      backdrop="static"
      centered
    >
      <Modal.Header>
        <Modal.Title id="edit-complaint-modal-title">Edit Complaint</Modal.Title>
        <i
          className="fa-solid fa-xmark close-container"
          onClick={handleClose}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") handleClose();
          }}
          aria-label="Close modal"
        ></i>
      </Modal.Header>
      <hr className="mt-4" />
      <Modal.Body>
        <Formik
          innerRef={formikRef}
          initialValues={emptyFormValues}
          validationSchema={validationSchema}
          enableReinitialize={false}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, setValues, submitForm, validateForm, setTouched }) => {
            const mapCenter =
              values.latitude && values.longitude
                ? {
                  lat: Number(values.latitude),
                  lng: Number(values.longitude),
                }
                : currentLocation || DEFAULT_MAP_CENTER;

            const handleValidatedSubmit = async () => {
              if (isSaving) return;

              const errors = await validateForm();
              if (errors && Object.keys(errors).length > 0) {
                setTouched(markAllFieldsTouched(emptyFormValues), true);

                const firstErrorKey = Object.keys(errors)[0];
                document
                  .querySelector(`[name="${firstErrorKey}"]`)
                  ?.scrollIntoView({ behavior: "smooth", block: "center" });
                return;
              }

              await submitForm();
            };

            return (
              <Form
                onSubmit={(event) => {
                  event.preventDefault();
                  handleValidatedSubmit();
                }}
              >
                <h6 className="mb-0" style={sectionHeaderStyle}>
                  Basic Information
                </h6>

                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label htmlFor="cgNo" className="form-label">
                      Complaint No {requiredMark}
                    </label>
                    <Field
                      id="cgNo"
                      name="cgNo"
                      className="form-control bg-light"
                      placeholder="Complaint No"
                      readOnly
                    />
                    <ErrorMessage
                      className="required-message"
                      name="cgNo"
                      component="div"
                    />
                  </div>

                  <div className="col-md-4 mb-3">
                    <label htmlFor="wardId" className="form-label">
                      Revised Ward {requiredMark}
                    </label>
                    <Select
                      inputId="wardId"
                      name="wardId"
                      classNamePrefix="react-select"
                      options={wards.map((ward) => ({
                        value: getWardOptionValue(ward),
                        label: ward.wardName || ward.circleName || ward.wardNo,
                      }))}
                      value={
                        wards
                          .map((ward) => ({
                            value: getWardOptionValue(ward),
                            label: ward.wardName || ward.circleName || ward.wardNo,
                          }))
                          .find((option) => option.value === values.wardId) || null
                      }
                      onChange={(selected) => {
                        const wardId = normalizeWardIdForPayload(
                          selected ? selected.value : ""
                        );
                        setValues({
                          ...values,
                          wardId,
                          zoneId: getZoneIdFromWard(wardId),
                        });
                      }}
                      placeholder="Select Ward"
                      isClearable={false}
                      menuPlacement="auto"
                      menuPortalTarget={document.body}
                      styles={SELECT_MENU_PORTAL_STYLES}
                    />
                    <ErrorMessage
                      className="required-message"
                      name="wardId"
                      component="div"
                    />
                  </div>

                  <div className="col-md-4 mb-3">
                    <label htmlFor="streetLocation" className="form-label">
                      Revised Street Location{requiredMark}
                    </label>
                    <Field
                      id="streetLocation"
                      name="streetLocation"
                      className="form-control"
                      placeholder="Enter Street Location"
                    />
                    <ErrorMessage
                      className="required-message"
                      name="streetLocation"
                      component="div"
                    />
                  </div>

                  <FileUploadField
                    label="Upload Closing Image"
                    selectedFileName={selectedFileName}
                    setSelectedFileName={setSelectedFileName}
                    isChooseFileHovered={isChooseFileHovered}
                    setIsChooseFileHovered={setIsChooseFileHovered}
                    setFieldValue={setFieldValue}
                  />

                  <div className="col-md-4 mb-3">
                    <label htmlFor="status" className="form-label">
                      Status
                    </label>
                    <Field
                      as="select"
                      id="status"
                      name="status"
                      className="form-select"
                    >
                      {complaintOptions.complaintFilterStatusOptions.map(
                        (status) => (
                          <option key={status.label} value={status.value}>
                            {status.label}
                          </option>
                        )
                      )}
                    </Field>
                    <ErrorMessage
                      className="required-message"
                      name="status"
                      component="div"
                    />
                  </div>

                  <div className="col-md-12 mb-3">
                    <label htmlFor="actionRemarks" className="form-label">
                      Action Remarks {requiredMark}
                    </label>
                    <Field
                      as="textarea"
                      id="actionRemarks"
                      name="actionRemarks"
                      rows="3"
                      maxLength={1000}
                      className="form-control"
                      placeholder="Enter remarks"
                    />
                    <ErrorMessage
                      className="required-message"
                      name="actionRemarks"
                      component="div"
                    />
                  </div>

                  <div className="col-md-12 mb-3">
                    <label htmlFor="address" className="form-label">
                      Address {requiredMark}
                    </label>
                    <Field
                      as="textarea"
                      id="address"
                      name="address"
                      rows="3"
                      maxLength={500}
                      className="form-control"
                      placeholder="Enter complete address"
                    />
                    <ErrorMessage
                      className="required-message"
                      name="address"
                      component="div"
                    />
                  </div>
                </div>

                <h6 className="mb-0 mt-2" style={sectionHeaderStyle}>
                  Map View
                </h6>
                {mapReady ? (
                  <ComplaintMapView
                    latitude={values.latitude}
                    longitude={values.longitude}
                    location={values.streetLocation || values.address}
                    height="320px"
                    showDefaultMap
                    defaultCenter={mapCenter}
                    locationLoading={locationLoading}
                    onLocationSelect={(lat, lng, address) => {
                      userPickedLocationRef.current = true;
                      setFieldValue("latitude", lat);
                      setFieldValue("longitude", lng);
                      if (address) {
                        setFieldValue("address", address);
                      }
                    }}
                  />
                ) : (
                  <div
                    className="d-flex align-items-center justify-content-center text-muted"
                    style={{
                      height: "320px",
                      border: "1px solid #E0E0E0",
                      borderRadius: 8,
                      background: "#F8FAFC",
                    }}
                  >
                    {locationLoading
                      ? "Detecting current location..."
                      : "Loading map..."}
                  </div>
                )}

                <hr />
                <div className="d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    style={cancelButtonStyle}
                    onClick={handleClose}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={saveButtonStyle}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <l-bouncy size="22" speed="1.75" color="white"></l-bouncy>
                    ) : (
                      "Update"
                    )}
                  </button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default EditComplaintForm;
